import React from 'react';
import _ from 'lodash';

import {
  RouteObject,
  useRoutes
} from 'react-router-dom';
import {
  createTheme,
  CssBaseline,
  ThemeProvider
} from '@mui/material';
import {
  isProd,
  SystemAlert
} from 'modules';

import { ApolloProvider } from '@apollo/client';
import { routes as adminRoutes } from './activities/admin';
import { routes as authRoutes } from './activities/auth';
import { routes as customerRoutes } from './activities/customer';
import { routes as partnerRoutes } from './activities/partner';
import { routes as commonRoutes } from './activities/common';
import { useAuthAwareApolloClient } from 'modules';



const theme = createTheme({
  palette: {
    background: {
      default: isProd ? '#EBECF1' : '#00000055'
    },
    primary: {
      '100': '#4060D3',
      light: '#F0F5FF',
      main: '#4060D3'
    },
    success: {
      light: '#E0FAED',
      main: '#51A77E'
    },
    secondary: {
      main: '#adadad'
    },
    warning: {
      light: '#FFF8E6',
      main: '#F4A60F'
    },
    error: {
      light: '#FCEAEF',
      main: '#E8446B'
    }
  },
  components: {
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '24px',
          fontFamily: 'Roboto',
          lineHeight: 1.2,
          fontWeight: '300'
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontSize: 13
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '32px'
        },
        h2: {
          fontSize: '24px'
        },
        caption: {
          fontSize: 12,
          // The React modules library does not provide access to the helperText prop for text fields, so we have to stylize
          // the Typography component to move it up artificially.
          marginTop: -20
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        outlinedInfo: {
          border: '1px solid #ECECEC',
          background: '#FAFAFA',
          color: '#3A3B3D',
          textTransform: 'none'
        },
        contained: {
          textTransform: 'none'
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          backgroundColor: 'white'
        }
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          minWidth: 100
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      }
    }
  }
});



const __routeObjects: RouteObject[] = [
  ...authRoutes,
  ...adminRoutes,
  ...customerRoutes,
  ...partnerRoutes,
  ...commonRoutes
];



export const App: React.FC = () => {
  const client = useAuthAwareApolloClient();

  const elRoutes = useRoutes(_.map(__routeObjects, (route): RouteObject => ({
    ...route,
    caseSensitive: true
  })));



  return (
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {elRoutes}

        <SystemAlert.FC />
      </ThemeProvider>
    </ApolloProvider>
  );
};
